import axios from "axios";
import appConfig from "../config/appConfig";
import { BranchPosition } from "../Languages/vars/BranchPosition";
import { Roles } from "../Languages/vars/Roles";
import { FilterRoles } from "../Languages/vars/FilterRoles";

/** @typedef {{ id: string, type: 'personal'|'company', name_en: string, name_de: string, category_en?: string, category_de?: string }} CustomBranch */

let cache = /** @type {CustomBranch[]|null} */ (null);
let inflight = /** @type {Promise<CustomBranch[]>|null} */ (null);

/**
 * Load admin-created industry branches from the API (cached).
 * Safe to call before login — endpoint is public (no auth header).
 */
export async function loadCustomBranches(force = false) {
  if (!force && cache) return cache;
  if (!force && inflight) return inflight;

  inflight = (async () => {
    try {
      const { data } = await axios.get(`${appConfig.API_URL}/branches`);
      cache =
        data && data.status && Array.isArray(data.branches) ? data.branches : [];
    } catch (e) {
      console.error("Failed to load custom branches:", e);
      if (!cache) cache = [];
    } finally {
      inflight = null;
    }
    return cache || [];
  })();

  return inflight;
}

export function getPersonalBranchPosition() {
  return mergePersonalBranches(BranchPosition);
}

export function getCompanyRoles() {
  return mergeCompanyBranches(Roles);
}

export function getFilterRoles() {
  // Feed sector filter includes both company industries and personal roles.
  let merged = mergeCompanyBranches(FilterRoles);
  const personalCustoms = getCachedCustomBranches("personal");
  if (!personalCustoms.length) return merged;

  merged = merged.map((cat) => ({
    ...cat,
    branches: [...(cat.branches || [])],
  }));
  const seen = new Set();
  for (const cat of merged) {
    for (const branch of cat.branches) {
      seen.add(String(branch.name_en || "").toLowerCase());
    }
  }
  let others = merged.find((c) => c.name_en === "Others");
  if (!others) {
    others = {
      id: "custom-others-personal",
      name_de: "Andere",
      name_en: "Others",
      branches: [],
    };
    merged.push(others);
  }
  for (const custom of personalCustoms) {
    const key = custom.name_en.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    let target = merged.find(
      (c) => c.name_en === (custom.category_en || "Others")
    );
    if (!target) target = others;
    target.branches.push({
      id: `custom-p-${custom.id}`,
      name_de: custom.name_de,
      name_en: custom.name_en,
    });
  }
  return merged;
}

export function getCachedCustomBranches(type) {
  const all = cache || [];
  if (!type) return all;
  return all.filter((b) => b.type === type);
}

/**
 * Label from custom catalogue translations for any interface language.
 * @param {string} nameEn stored English (or DE) name
 * @param {string|boolean|null} langOrIsGerm language code, or legacy boolean isGerm
 */
export function customBranchLabel(nameEn, langOrIsGerm) {
  const key = String(nameEn || "")
    .trim()
    .toLowerCase();
  if (!key || !cache) return null;
  const found = cache.find(
    (b) =>
      b.name_en.trim().toLowerCase() === key ||
      (b.name_de && b.name_de.trim().toLowerCase() === key)
  );
  if (!found) return null;

  let code;
  if (typeof langOrIsGerm === "boolean") {
    code = langOrIsGerm ? "de" : "en";
  } else {
    code = String(langOrIsGerm || "en")
      .toLowerCase()
      .split("-")[0];
  }
  if (code === "gsw" || code === "wae") code = "de";

  const fromMap = found.translations && found.translations[code];
  if (fromMap) return fromMap;
  if (code === "de") return found.name_de;
  return found.name_en;
}

/**
 * Merge custom personal branches into a BranchPosition-like tree
 * (categories with Roles[]). Mutates a shallow copy; does not mutate source.
 */
export function mergePersonalBranches(branchPosition) {
  const customs = getCachedCustomBranches("personal");
  if (!customs.length) return branchPosition;

  const categories = branchPosition.map((cat) => ({
    ...cat,
    Roles: [...(cat.Roles || [])],
  }));

  const seen = new Set();
  for (const cat of categories) {
    for (const role of cat.Roles) {
      seen.add(String(role.name_en || "").toLowerCase());
    }
  }

  let others = categories.find((c) => c.name_en === "Others");
  if (!others) {
    others = {
      id: "custom-others",
      name_de: "Andere",
      name_en: "Others",
      Roles: [],
    };
    categories.push(others);
  }

  for (const custom of customs) {
    const key = custom.name_en.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    let target = categories.find(
      (c) => c.name_en === (custom.category_en || "Others")
    );
    if (!target) target = others;

    target.Roles.push({
      id: `custom-${custom.id}`,
      name_de: custom.name_de,
      name_en: custom.name_en,
    });
  }

  return categories;
}

/**
 * Merge custom company branches into a Roles-like tree
 * (categories with branches[]).
 */
export function mergeCompanyBranches(rolesTree) {
  const customs = getCachedCustomBranches("company");
  if (!customs.length) return rolesTree;

  const categories = rolesTree.map((cat) => ({
    ...cat,
    branches: [...(cat.branches || [])],
  }));

  const seen = new Set();
  for (const cat of categories) {
    for (const branch of cat.branches) {
      seen.add(String(branch.name_en || "").toLowerCase());
    }
  }

  let others = categories.find((c) => c.name_en === "Others");
  if (!others) {
    others = {
      id: "custom-others",
      name_de: "Andere",
      name_en: "Others",
      branches: [],
    };
    categories.push(others);
  }

  for (const custom of customs) {
    const key = custom.name_en.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    let target = categories.find(
      (c) => c.name_en === (custom.category_en || "Others")
    );
    if (!target) target = others;

    target.branches.push({
      id: `custom-${custom.id}`,
      name_de: custom.name_de,
      name_en: custom.name_en,
    });
  }

  return categories;
}

/** Flat {name_en, name_de} list for convertBranchRole lookups. */
export function getCustomBranchesFlat(type) {
  return getCachedCustomBranches(type).map((b) => ({
    id: `custom-${b.id}`,
    name_en: b.name_en,
    name_de: b.name_de,
  }));
}
